import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import DateObject from 'react-date-object'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import DatePicker from 'react-multi-date-picker'

import {
  AUTO_STATS_EVENT_TYPES,
  AutoStatsEvent,
  AutoStatsEventType,
  useAutoStatsEvents,
} from '../api'
import { useToken } from '../App'
import { strings } from '../strings'
import { formatDate, formatDuration } from '../format-helpers'
import { CloseIcon, RefreshIcon } from '../icons'
import { downloadCsv } from './Analytics/csv'

const ADMIN_PAGE_SIZE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 100, label: '100' },
  { value: 500, label: '500' },
  { value: 1000, label: '1000' },
]

const OPERATOR_PAGE_SIZE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 100, label: '100' },
]

const EVENT_LABEL: Record<AutoStatsEventType, string> = {
  base_connected: strings.baseConnected,
  base_disconnected: strings.baseDisconnected,
  base_connect_failed: strings.baseConnectFailed,
  rover_connected: strings.roverConnected,
  rover_disconnected: strings.roverDisconnected,
  rover_connect_failed: strings.roverConnectFailed,
}

const EVENT_COLOR: Record<AutoStatsEventType, string> = {
  base_connected: '#1b5e20',
  base_disconnected: '#bf360c',
  base_connect_failed: '#b71c1c',
  rover_connected: '#0d47a1',
  rover_disconnected: '#4a148c',
  rover_connect_failed: '#b71c1c',
}

const EVENT_BG: Record<AutoStatsEventType, string> = {
  base_connected: '#e8f5e9',
  base_disconnected: '#ffe0b2',
  base_connect_failed: '#ffcdd2',
  rover_connected: '#e3f2fd',
  rover_disconnected: '#ede7f6',
  rover_connect_failed: '#ffcdd2',
}

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

function toDate(value: DateObject | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return value
  return value.toDate()
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function Events() {
  const classes = useStyles()
  const { ut: userRole } = useToken()
  const isAdmin = userRole === 'admin'
  const isAllowed = isAdmin || userRole === 'operator'
  const pageSizeOptions = isAdmin
    ? ADMIN_PAGE_SIZE_OPTIONS
    : OPERATOR_PAGE_SIZE_OPTIONS

  const [range, setRange] = useState<[DateObject, DateObject]>(() => {
    const now = new Date()
    return [
      new DateObject(startOfDay(now)).convert(persian, persian_fa),
      new DateObject(endOfDay(now)).convert(persian, persian_fa),
    ]
  })
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [mountpoint, setMountpoint] = useState('')
  const [eventTypes, setEventTypes] = useState<AutoStatsEventType[]>([])
  const [pageSize, setPageSize] = useState<number>(100)
  const [page, setPage] = useState(0)

  const usernameDeb = useDebouncedValue(username.trim(), 400)
  const displayNameDeb = useDebouncedValue(displayName.trim(), 400)
  const phoneNumberDeb = useDebouncedValue(phoneNumber.trim(), 400)
  const mountpointDeb = useDebouncedValue(mountpoint.trim(), 400)

  useEffect(() => {
    setPage(0)
  }, [
    usernameDeb,
    displayNameDeb,
    phoneNumberDeb,
    mountpointDeb,
    eventTypes,
  ])

  const fromIso = useMemo(() => {
    const d = toDate(range[0])
    return d ? startOfDay(d).toISOString() : null
  }, [range])
  const toIso = useMemo(() => {
    const d = toDate(range[1] ?? range[0])
    return d ? endOfDay(d).toISOString() : null
  }, [range])

  const { events, total, totalPages, loading, error, refresh } =
    useAutoStatsEvents(fromIso, toIso, page + 1, pageSize, {
      event: eventTypes,
      username: usernameDeb,
      displayName: displayNameDeb,
      phoneNumber: phoneNumberDeb,
      mountpoint: mountpointDeb,
    })

  const [causeAnchor, setCauseAnchor] = useState<HTMLElement | null>(null)

  const sorted = useMemo(() => {
    if (!events) return null
    return [...events].sort((a, b) => b.unix_time - a.unix_time)
  }, [events])

  const hasFilters =
    username !== '' ||
    displayName !== '' ||
    phoneNumber !== '' ||
    mountpoint !== '' ||
    eventTypes.length > 0

  const clearAllFilters = () => {
    setUsername('')
    setDisplayName('')
    setPhoneNumber('')
    setMountpoint('')
    setEventTypes([])
  }

  const clearAdornment = (clear: () => void, visible: boolean) =>
    visible
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={clear}
                className={classes.clearBtn}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }
      : undefined

  const pageCount = Math.max(1, totalPages)
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = sorted ?? []

  const onExport = () => {
    if (!sorted) return
    const headers = [
      strings.eventType,
      strings.date,
      strings.username,
      strings.fullname,
      strings.phoneNumber,
      strings.mountpoint,
      strings.baseOwner,
      strings.userAgent,
      strings.sourceAgent,
      strings.remoteIp,
      strings.disconnectCause,
      strings.durationSeconds,
      strings.rtcmCount,
    ]
    const rows = sorted.map((e) => [
      EVENT_LABEL[e.event] ?? e.event,
      formatDate(e.timestamp),
      e.username || '',
      e.display_name || '',
      e.phone_number || '',
      e.mountpoint || '',
      e.base_owner_display_name || e.base_owner_username || '',
      e.user_agent || '',
      e.source_agent || '',
      e.remote || '',
      e.disconnect_cause || '',
      e.duration_seconds ?? '',
      e.rtcm_count ?? '',
    ])
    downloadCsv(`events-${fromIso?.slice(0, 10) ?? ''}.csv`, headers, rows)
  }

  if (!isAllowed) {
    return (
      <Paper className={classes.paper}>
        <div className={classes.empty}>
          <Typography color="error">{strings.unauthorized}</Typography>
        </div>
      </Paper>
    )
  }

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <Typography className={classes.title}>{strings.events}</Typography>
        <div className={classes.headerRight}>
          <Typography variant="body2" className={classes.recordsLabel}>
            {strings.totalRecords}: {total.toLocaleString('en-US')}
          </Typography>
          <Tooltip title={strings.refresh} arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                className={classes.rtlBtn}
                onClick={() => refresh()}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={14} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
                {strings.refresh}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={strings.disconnectCauseHelp} arrow>
            <Button
              variant="outlined"
              size="small"
              className={classes.rtlBtn}
              onClick={(e) => setCauseAnchor(e.currentTarget)}
            >
              <InfoOutlinedIcon fontSize="small" />
              {strings.disconnectCauseHelp}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={clearAllFilters}
            disabled={!hasFilters}
          >
            {strings.clearFilters}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onExport}
            disabled={!sorted || sorted.length === 0}
          >
            {strings.exportCsv}
          </Button>
        </div>
        <Popover
          open={Boolean(causeAnchor)}
          anchorEl={causeAnchor}
          onClose={() => setCauseAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <div className={classes.helpPopover}>
            <Typography className={classes.helpTitle}>
              {strings.disconnectCauseHelpTitle}
            </Typography>
            <Typography className={classes.helpSubTitle}>
              {strings.disconnectCauseHelpBaseTitle}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseConnectionResetBase}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseIoTimeoutBase}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseEofBase}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseInvalidUserBase}
            </Typography>
            <Typography className={classes.helpSubTitle}>
              {strings.disconnectCauseHelpRoverTitle}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseConnectionResetRover}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseIoTimeoutRover}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseEofRover}
            </Typography>
            <Typography className={classes.helpItem}>
              {strings.disconnectCauseInvalidUserRover}
            </Typography>
          </div>
        </Popover>
      </div>

      <div className={classes.controls}>
        <div className={classes.controlRow}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder={strings.fullname}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            InputProps={clearAdornment(() => setDisplayName(''), !!displayName)}
          />
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder={strings.username}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputProps={clearAdornment(() => setUsername(''), !!username)}
          />
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder={strings.phoneNumber}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            InputProps={clearAdornment(() => setPhoneNumber(''), !!phoneNumber)}
          />
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder={strings.mountpoint}
            value={mountpoint}
            onChange={(e) => setMountpoint(e.target.value)}
            InputProps={clearAdornment(() => setMountpoint(''), !!mountpoint)}
          />
          <Autocomplete
            multiple
            size="small"
            className={classes.typeFilter}
            options={AUTO_STATS_EVENT_TYPES as unknown as AutoStatsEventType[]}
            value={eventTypes}
            onChange={(_e, value) => {
              setEventTypes(value as AutoStatsEventType[])
            }}
            getOptionLabel={(o) => EVENT_LABEL[o] ?? o}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={EVENT_LABEL[option] ?? option}
                  {...getTagProps({ index })}
                  style={{
                    backgroundColor: EVENT_BG[option],
                    color: EVENT_COLOR[option],
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  eventTypes.length === 0 ? strings.allEvents : undefined
                }
                label={strings.eventTypes}
              />
            )}
          />
          <div className={classes.dateWrap}>
            {isAdmin ? (
              <DatePicker
                range
                calendar={persian}
                locale={persian_fa}
                digits={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
                value={range}
                onChange={(value) => {
                  if (Array.isArray(value) && value.length === 2) {
                    setRange(value as [DateObject, DateObject])
                    setPage(0)
                  }
                }}
                format="YYYY/MM/DD"
                maxDate={new Date()}
                render={(value, openCalendar) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    label={`${strings.from} - ${strings.to}`}
                    value={value as string}
                    onClick={openCalendar}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                )}
              />
            ) : (
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                digits={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
                value={range[0]}
                onChange={(value) => {
                  const d = value as DateObject | null
                  if (!d) return
                  setRange([d, d])
                  setPage(0)
                }}
                format="YYYY/MM/DD"
                maxDate={new Date()}
                render={(value, openCalendar) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    label={strings.date}
                    value={value as string}
                    onClick={openCalendar}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                )}
              />
            )}
          </div>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.pageSize}
            disabled={pageSizeOptions.length <= 1}
          >
            <InputLabel>{strings.pageSize}</InputLabel>
            <Select
              value={pageSize}
              label={strings.pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(0)
              }}
            >
              {pageSizeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className={classes.tableWrap}>
        {loading && (
          <div className={classes.empty}>
            <CircularProgress size={24} />
            <Typography>{strings.loading}</Typography>
          </div>
        )}
        {error && !loading && (
          <div className={classes.empty}>
            <Typography color="error">
              {strings.error}: {(error as any)?.message ?? String(error)}
            </Typography>
          </div>
        )}
        {!loading && !error && sorted && sorted.length === 0 && (
          <div className={classes.empty}>
            <Typography>{strings.noEventsInRange}</Typography>
          </div>
        )}
        {!loading && !error && sorted && sorted.length > 0 && (
          <table className={classes.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>{strings.eventType}</th>
                <th>{strings.date}</th>
                <th>{strings.fullname}</th>
                <th>{strings.username}</th>
                <th>{strings.phoneNumber}</th>
                <th>{strings.mountpoint}</th>
                <th>{strings.baseOwner}</th>
                <th>{strings.userAgent}</th>
                <th>{strings.sourceAgent}</th>
                <th>{strings.remoteIp}</th>
                <th>{strings.duration}</th>
                <th>{strings.disconnectCause}</th>
                <th>{strings.rtcmCount}</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((e, i) => (
                <EventRow
                  key={`${e.unix_time}-${i}`}
                  ev={e}
                  rowNumber={safePage * pageSize + i + 1}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && !error && total > 0 && (
        <div className={classes.pagination}>
          <Button
            size="small"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹
          </Button>
          <Typography variant="body2">
            {strings.showing} {safePage * pageSize + 1}–
            {Math.min(total, (safePage + 1) * pageSize)} /{' '}
            {total.toLocaleString('en-US')}
          </Typography>
          <Button
            size="small"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            ›
          </Button>
        </div>
      )}
    </Paper>
  )
}

function EventRow({
  ev,
  rowNumber,
}: {
  ev: AutoStatsEvent
  rowNumber: number
}) {
  const classes = useStyles()
  return (
    <tr>
      <td className={classes.nowrap}>{rowNumber.toLocaleString('en-US')}</td>
      <td>
        <span
          className={classes.badge}
          style={{
            backgroundColor: EVENT_BG[ev.event],
            color: EVENT_COLOR[ev.event],
          }}
        >
          {EVENT_LABEL[ev.event] ?? ev.event}
        </span>
      </td>
      <td className={classes.nowrap} style={{ unicodeBidi: 'plaintext' }}>
        {formatDate(ev.timestamp)}
      </td>
      <td>
        <Tooltip title={ev.display_name || ''} arrow>
          <span className={classes.ellipsis}>{ev.display_name || '—'}</span>
        </Tooltip>
      </td>
      <td>
        <span className={classes.ellipsis}>{ev.username || '—'}</span>
      </td>
      <td className={classes.nowrap}>{ev.phone_number || '—'}</td>
      <td>
        <Tooltip title={ev.mountpoint || ''} arrow>
          <span className={classes.ellipsis}>{ev.mountpoint || '—'}</span>
        </Tooltip>
      </td>
      <td>
        <Tooltip
          title={ev.base_owner_display_name || ev.base_owner_username || ''}
          arrow
        >
          <span className={classes.ellipsis}>
            {ev.base_owner_display_name || ev.base_owner_username || '—'}
          </span>
        </Tooltip>
      </td>
      <td>
        <Tooltip title={ev.user_agent || ''} arrow>
          <span
            className={classes.ellipsis}
            style={{ direction: 'ltr', maxWidth: 220 }}
          >
            {ev.user_agent || '—'}
          </span>
        </Tooltip>
      </td>
      <td>
        <Tooltip title={ev.source_agent || ''} arrow>
          <span className={classes.ellipsis}>{ev.source_agent || '—'}</span>
        </Tooltip>
      </td>
      <td className={classes.nowrap} style={{ direction: 'ltr' }}>
        {ev.remote || '—'}
      </td>
      <td className={classes.nowrap}>
        {ev.duration_seconds != null ? formatDuration(ev.duration_seconds) : '—'}
      </td>
      <td>
        <span className={classes.ellipsis}>{ev.disconnect_cause || '—'}</span>
      </td>
      <td className={classes.nowrap}>
        {ev.rtcm_count != null ? ev.rtcm_count.toLocaleString('en-US') : '—'}
      </td>
    </tr>
  )
}

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'relative',
    width: `calc(100dvw - ${theme.spacing(2)}px)`,
    height: `calc(100dvh - 48px - ${theme.spacing(3)}px)`,
    padding: theme.spacing(1),
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
    background: 'silver',
    borderRadius: 4,
    margin: -theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  recordsLabel: {
    color: '#222',
  },
  rtlBtn: {
    '& .MuiButton-label': {
      flexDirection: 'row-reverse',
      gap: theme.spacing(0.75),
    },
  },
  clearBtn: {
    padding: 2,
  },
  title: {
    fontWeight: 600,
  },
  controls: {
    flex: '0 0 auto',
    padding: theme.spacing(0.5, 0.5, 1),
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  searchField: {
    flex: '1 1 160px',
    minWidth: 140,
  },
  typeFilter: {
    flex: '1 1 280px',
    minWidth: 240,
  },
  dateWrap: {
    flex: '1 1 240px',
    minWidth: 240,
  },
  pageSize: {
    minWidth: 140,
  },
  tableWrap: {
    flex: '1 1 auto',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  table: {
    width: '100%',
    minWidth: 'max-content',
    borderCollapse: 'collapse',
    '& th': {
      position: 'sticky',
      top: 0,
      background: '#fafafa',
      borderBottom: '1px solid #e0e0e0',
      fontWeight: 600,
      textAlign: 'left',
      padding: theme.spacing(0.75, 1),
      whiteSpace: 'nowrap',
      zIndex: 1,
    },
    '& td': {
      padding: theme.spacing(0.5, 1),
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
      maxWidth: 260,
    },
    '& tr:hover td': {
      backgroundColor: '#fafafa',
    },
  },
  ellipsis: {
    display: 'inline-block',
    maxWidth: 240,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  empty: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  pagination: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
  helpPopover: {
    padding: theme.spacing(2),
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  helpTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    paddingBottom: theme.spacing(0.5),
    borderBottom: '1px solid #e0e0e0',
  },
  helpSubTitle: {
    fontWeight: 600,
    color: '#444',
    marginTop: theme.spacing(0.5),
  },
  helpItem: {
    fontSize: '0.825rem',
    lineHeight: 1.7,
    color: '#333',
  },
}))

export default Events
